import { useMemo, useState } from 'react'
import {
  Box,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Virtuoso } from 'react-virtuoso'

import Server from '../types/Server'
import EccChip from './EccChip'
import { grossPrice, formatEuro, diskSummary, serverKey, PROVIDER_NAMES } from '../utils/servers'

type SortKey = 'price-asc' | 'price-desc' | 'ram-asc' | 'ram-desc'

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'price-asc', label: 'Price ↑' },
  { value: 'price-desc', label: 'Price ↓' },
  { value: 'ram-asc', label: 'RAM ↑' },
  { value: 'ram-desc', label: 'RAM ↓' },
]

const COMPARATORS: Record<SortKey, (a: Server, b: Server) => number> = {
  'price-asc': (a, b) => grossPrice(a) - grossPrice(b),
  'price-desc': (a, b) => grossPrice(b) - grossPrice(a),
  'ram-asc': (a, b) => a.ram_size - b.ram_size,
  'ram-desc': (a, b) => b.ram_size - a.ram_size,
}

type ServerCardProps = {
  server: Server
  onSelect: (server: Server) => void
}

const ServerCard: React.FC<ServerCardProps> = ({ server, onSelect }) => {
  const disks = server.serverDiskData
  return (
    <Paper
      variant="outlined"
      onClick={() => onSelect(server)}
      sx={{ p: 1.5, mb: 1, cursor: 'pointer' }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {server.cpu}
        </Typography>
        <Typography
          variant="subtitle1"
          color="primary"
          sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          {formatEuro(grossPrice(server))}
        </Typography>
      </Stack>
      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        <Chip size="small" label={`${server.ram_size} GB RAM`} />
        {server.is_ecc && <EccChip />}
        {disks.nvme.length > 0 && <Chip size="small" label={`NVMe ${diskSummary(disks.nvme)}`} />}
        {disks.sata.length > 0 && <Chip size="small" label={`SATA ${diskSummary(disks.sata)}`} />}
        {disks.hdd.length > 0 && <Chip size="small" label={`HDD ${diskSummary(disks.hdd)}`} />}
        <Chip
          size="small"
          variant="outlined"
          label={`${PROVIDER_NAMES[server.provider]} · ${server.datacenter}`}
        />
      </Box>
    </Paper>
  )
}

type ServerCardsProps = {
  servers: Server[]
  onSelect: (server: Server) => void
}

const ServerCards: React.FC<ServerCardsProps> = ({ servers, onSelect }) => {
  const [sort, setSort] = useState<SortKey>('price-asc')

  const sorted = useMemo(() => [...servers].sort(COMPARATORS[sort]), [servers, sort])

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <TextField
          select
          size="small"
          label="Sort by"
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          sx={{ minWidth: 130 }}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Virtuoso
        useWindowScroll
        data={sorted}
        computeItemKey={(_index, server) => serverKey(server)}
        itemContent={(_index, server) => <ServerCard server={server} onSelect={onSelect} />}
      />
    </>
  )
}

export default ServerCards
